<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { usePedidoStore } from '@/stores/pedido';
import { useFormaPagamentoStore } from '@/stores/formaPagamento';
import usePedidoActions from '@/composables/usePedidoActions';
import useColumnManagement from '@/composables/useColumnManagement';
import useModalManagement from '@/composables/useModalManagement';
import useColumnUpdater from '@/composables/useColumnUpdater';
import PedidoColumnView from '@/components/pedidos/PedidoColumnView.vue';
import DesativarRevendedorModal from '@/components/modals/DesativarRevendedorModal.vue';
import { origemOptions, estadosOptions, categoriaProdutoOptions } from '@/utils/filtros.js';

// Initialize stores
const pedidoStore = usePedidoStore();
const formaPagamentoStore = useFormaPagamentoStore();

// Initialize composables
const {
  showCopyNotification,
  copyMessage,
  loadingRevendedorAction,
  desativarRevenda,
} = usePedidoActions();

const {
  columnSettingsMenuOpen,
  draggedColumn,
  onDragStart,
  onDragOver,
  onDrop,
  toggleColumnVisibility
} = useColumnManagement(pedidoStore);

const {
  editOrderModalOpen,
  selectedPedido,
  editedPedido,
  editOrderLoading,
  observationModalOpen,
  observationData,
  assistanceModalOpen,
  assistanceData,
  assistanceLoading,

  saveEditedOrder,
  cancelOrderEdit,
  salvarObservacao,
  closeObservationModal,
  solicitarAssistencia,
  cancelAssistance
} = useModalManagement();

const { startColumnUpdate, stopAllUpdates } = useColumnUpdater();

// UI state
const filtersExpanded = ref([1]);

// Setup & cleanup
const initializeData = async () => {
  try {
    await formaPagamentoStore.fetchFormasPagamento();
    pedidoStore.initialize();
  } catch (err) {
  }
};

// Adicione este hook para parar as atualizações quando o usuário navegar para longe da página
onBeforeRouteLeave((to, from, next) => {
  // Garantir que as atualizações automáticas sejam interrompidas ao sair da página
  stopAllUpdates();
  next();
});

onMounted(() => {
  initializeData();

  // Atualizar apenas as colunas visíveis com os intervalos configurados
  pedidoStore.visibleColumns.forEach((column) => {
    const interval = {
      aceitar: 25000, // 25 segundos
      atrasados: 60000, // 1 minuto
      suspeitos: 30000, // 30 segundos
      emandamento: 60000, // 1 minuto
      entregues: 120000, // 2 minutos
      cancelados: 150000, // 2.5 minutos
      mereceatencao: 60000, // 1 minuto
      atencao: 25000, // 25 segundos
    }[column.key] || 50000; // Padrão: 50 segundos

    startColumnUpdate(column.key, interval);
  });

  // Observar mudanças na visibilidade das colunas para iniciar ou parar atualizações
  watch(
    () => pedidoStore.visibleColumns,
    (newVisibleColumns, oldVisibleColumns) => {
      const newKeys = newVisibleColumns.map((col) => col.key);
      const oldKeys = oldVisibleColumns.map((col) => col.key);

      // Iniciar atualizações para colunas recém-habilitadas
      newKeys
        .filter((key) => !oldKeys.includes(key))
        .forEach((key) => {
          const interval = {
            aceitar: 25000, // 25 segundos
            atrasados: 60000, // 1 minuto
            suspeitos: 30000, // 30 segundos
            emandamento: 60000, // 1 minuto
            entregues: 120000, // 2 minutos
            cancelados: 150000, // 2.5 minutos
            mereceatencao: 60000, // 1 minuto
            atencao: 10000, // 10 segundos (nova coluna)
          }[key] || 50000; // Padrão: 50 segundos

          startColumnUpdate(key, interval);
        });

      // Parar atualizações para colunas desabilitadas
      oldKeys
        .filter((key) => !newKeys.includes(key))
        .forEach((key) => stopAllUpdates(key));
    },
    { deep: true }
  );


});

onBeforeUnmount(() => {
  stopAllUpdates();
});

// Keep this ref for the DesativarRevendedorModal
const deactivateModalOpen = ref(false);
// Initialize our own selectedRevenda with a default empty object
const selectedRevenda = ref({
  revendedor: {
    id: null,
    razaosocial: '',
    ativo: 0
  }
});

// Handle revendedor deactivation (from modal)
const handleRevendedorDeactivated = (updatedRevendedor) => {
  if (selectedRevenda.value?.revendedor) {
    selectedRevenda.value.revendedor.ativo = updatedRevendedor.ativo;
  }
};
</script>

<template>
  <div>
    <v-snackbar v-model="showCopyNotification" :timeout="2000" color="success" location="top">
      {{ copyMessage }}
    </v-snackbar>

    <!-- Snackbar modificado para erros -->
    <v-snackbar :model-value="pedidoStore.errorSnackbar.show" :timeout="pedidoStore.errorSnackbar.timeout"
      :color="pedidoStore.errorSnackbar.color" location="top"
      @update:model-value="(val) => !val && pedidoStore.closeErrorSnackbar()">
      {{ pedidoStore.errorSnackbar.message }}
    </v-snackbar>

    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between pt-0">
        <h1 class="text-h4 mb-2">Gerenciar Pedidos</h1>

        <!-- Unified column settings menu -->
        <v-menu v-model="columnSettingsMenuOpen" :close-on-content-click="false" location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn color="primary" variant="outlined" prepend-icon="mdi-cog-outline" v-bind="props">
              Configurar Colunas
            </v-btn>
          </template>

          <v-card min-width="320" class="pa-3">
            <v-card-title class="px-0 pt-0">Configurar colunas</v-card-title>
            <v-divider class="my-2"></v-divider>

            <p class="text-body-2 mb-3">Arraste para reordenar e clique no ícone para mostrar/ocultar:</p>

            <v-list density="compact" class="pa-0">
              <v-list-item v-for="(column, index) in pedidoStore.statusColumns" :key="column.key" :draggable="true"
                @dragstart="onDragStart(column)" @dragover="onDragOver" @drop="onDrop(column)"
                class="column-order-item mb-1" :class="{ 'column-order-dragging': draggedColumn?.key === column.key }">
                <v-list-item-title class="d-flex align-center">
                  <v-icon class="mr-2 drag-handle">mdi-drag</v-icon>
                  <div class="column-color-indicator mr-2" :style="`background-color: ${column.color}`"></div>
                  {{ column.title }}
                  <v-spacer></v-spacer>
                  <v-icon size="small" :color="column.visible ? 'primary' : 'grey'"
                    :icon="column.visible ? 'mdi-eye' : 'mdi-eye-off'" class="visibility-toggle"
                    @click="toggleColumnVisibility(column, index)"></v-icon>
                </v-list-item-title>
              </v-list-item>
            </v-list>

            <v-alert v-if="!pedidoStore.hasVisibleColumns" type="warning" variant="tonal" density="compact"
              class="mt-3 mb-0">
              Selecione pelo menos uma coluna para exibição
            </v-alert>

            <v-alert v-else type="info" variant="tonal" density="compact" class="mt-3 mb-0">
              Todas as alterações são salvas automaticamente
            </v-alert>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Filtros panel -->
    <v-expansion-panels v-model="filtersExpanded" class="mb-2">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row no-gutters>
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-filter</v-icon>
              <span class="text-subtitle-1">Filtros</span>
            </div>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <!-- Período -->
            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.dataInicio" label="Data de início" type="date"
                density="compact" hide-details class="mb-2"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.dataFim" label="Data de término" type="date" density="compact"
                hide-details class="mb-2"></v-text-field>
            </v-col>

            <!-- Status e Origem -->
            <v-col cols="12" md="3">
              <v-select v-model="pedidoStore.filtros.origem" :items="origemOptions" item-title="title"
                item-value="value" label="Origem" density="compact" multiple chips hide-details class="mb-2"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="pedidoStore.filtros.estados" :items="estadosOptions" item-title="title"
                item-value="value" label="Estado/UF" density="compact" multiple chips hide-details
                prepend-inner-icon="mdi-map-marker" class="mb-2"></v-select>
            </v-col>


            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.razaoSocial" label="Razão social" density="compact"
                hide-details prepend-inner-icon="mdi-domain" class="mb-2"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.nomeFantasia" label="Nome fantasia" density="compact"
                hide-details prepend-inner-icon="mdi-store" class="mb-2"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.numeroPedido" label="Número do pedido" density="compact"
                hide-details prepend-inner-icon="mdi-pound" class="mb-2"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="pedidoStore.filtros.telefoneCliente" v-mask="'(##) #####-####'"
                label="Telefone do cliente" density="compact" hide-details prepend-inner-icon="mdi-phone"
                class="mb-2"></v-text-field>
            </v-col>

            <!-- Checkbox filters -->
            <div class="d-flex align-center ga-4 pl-2 w-100 py-2">
              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.merecemAtencao" label="Merecem atenção"
                  hide-details density="compact" color="primary"></v-checkbox>
              </div>
              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.areaExterna" label="Área externa"
                  hide-details density="compact" color="primary"></v-checkbox>
              </div>
              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.agendados" label="Agendados" hide-details
                  density="compact" color="primary"></v-checkbox>
              </div>
              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.assistencia" label="Assistência"
                  hide-details density="compact" color="primary"></v-checkbox>
              </div>

              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.pendentes" label="Pendentes" hide-details
                  density="compact" color="primary"></v-checkbox>
              </div>

              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.semGestor" label="Sem gestor" hide-details
                  density="compact" color="primary"></v-checkbox>
              </div>

              <div>
                <v-checkbox class="text-caption" v-model="pedidoStore.filtros.gasDoPovo" label="Gás do povo" hide-details
                  density="compact" color="primary"></v-checkbox>
              </div>

              <div class="ml-auto d-flex ga-2 w-25 pl-2 mb-3">
                <div style="width: 200px;">
                  <v-select v-model="pedidoStore.filtros.gestor" :disabled="pedidoStore.filtros.semGestor"
                    :items="pedidoStore.managersColumns" item-title="title" item-value="value" label="Gestor"
                    density="compact" hide-details clearable clear-icon="mdi-close-circle"
                    @click:clear="pedidoStore.filtros.gestor = null" />
                </div>

                <div style="width: 200px;">
                  <v-select v-model="pedidoStore.filtros.categoriaProduto" :items="categoriaProdutoOptions"
                    item-title="title" item-value="value" label="Categoria" density="compact" hide-details clearable
                    clear-icon="mdi-close-circle" @click:clear="pedidoStore.filtros.categoriaProduto = null" />
                </div>
              </div>
            </div>

            <!-- Filter action buttons -->
            <v-col cols="12" class="d-flex justify-end pt-0">
              <v-btn color="error" variant="text" class="mr-2" @click="pedidoStore.limparFiltros"
                prepend-icon="mdi-refresh">
                Limpar
              </v-btn>
              <v-btn color="primary" @click="pedidoStore.aplicarFiltros" prepend-icon="mdi-filter-check">
                Aplicar filtros
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Error and loading indicators -->
    <v-alert v-if="pedidoStore.error" type="error" closable class="mb-4">
      {{ pedidoStore.error }}
    </v-alert>

    <v-progress-linear v-if="pedidoStore.loading" indeterminate color="primary"></v-progress-linear>

    <!-- Empty state when no columns are visible -->
    <v-card v-if="!pedidoStore.hasVisibleColumns" class="my-4 pa-4 text-center">
      <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-eye-off</v-icon>
      <h3 class="text-h5 mb-2">Nenhuma coluna selecionada</h3>
      <p class="text-body-1 mb-4">Selecione pelo menos uma coluna para visualizar os pedidos</p>
      <v-btn color="primary" prepend-icon="mdi-eye" @click="pedidoStore.toggleAllColumns(true)">
        Exibir todas as colunas
      </v-btn>
    </v-card>

    <!-- Kanban board -->
    <div v-else class="kanban-container">
      <div class="kanban-wrapper">
        <PedidoColumnView v-for="column in pedidoStore.visibleColumns" :key="column.key" :column="column" />
      </div>
    </div>

    <!-- Modal desativar revenda - Add null check for revendedor -->
    <DesativarRevendedorModal v-if="selectedRevenda && selectedRevenda.revendedor" v-model:open="deactivateModalOpen"
      :revendedor="selectedRevenda.revendedor" @desativado="handleRevendedorDeactivated" />

    <!-- Modal editar pedido -->
    <v-dialog v-model="editOrderModalOpen" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white">
          <v-icon start icon="mdi-pencil" class="mr-2"></v-icon>
          Editar Informações do Pedido #{{ selectedPedido?.id }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form @submit.prevent="saveEditedOrder">
            <v-row>
              <!-- Data/Hora Previsão -->
              <v-col cols="12" md="6">
                <v-text-field v-model="editedPedido.dtprevisao" label="Data Previsão" type="date" density="compact"
                  variant="outlined" hide-details="auto" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="editedPedido.horaprevisao" label="Hora Previsão" type="time" density="compact"
                  variant="outlined" hide-details="auto" class="mb-3"></v-text-field>
              </v-col>

              <!-- Endereço -->
              <v-col cols="12" md="6">
                <v-text-field v-model="editedPedido.numero" label="Número" type="text" density="compact"
                  variant="outlined" hide-details="auto" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="editedPedido.complemento" label="Complemento" type="text" density="compact"
                  variant="outlined" hide-details="auto" class="mb-3"></v-text-field>
              </v-col>

              <!-- Pagamento -->
              <v-col cols="12" md="6">
                <v-text-field v-model.number="editedPedido.vlpedido" label="Valor do Pedido" type="number"
                  density="compact" variant="outlined" hide-details="auto" prefix="R$" step="0.01" min="0"
                  class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="editedPedido.idformapagamento" :items="formaPagamentoStore.data" item-title="title"
                  item-value="value" label="Forma de Pagamento" density="compact" variant="outlined" hide-details="auto"
                  class="mb-3"></v-select>
              </v-col>

              <!-- Troco (para pagamento em dinheiro) -->
              <v-col cols="12" md="6" v-if="editedPedido.idformapagamento === 1">
                <v-text-field v-model.number="editedPedido.troco" label="Troco" type="number" density="compact"
                  variant="outlined" hide-details="auto" prefix="R$" step="0.01" min="0" class="mb-3"></v-text-field>
              </v-col>

              <!-- Parcelas (para pagamento em cartão) -->
              <v-col cols="12" md="6" v-if="editedPedido.idformapagamento === 2">
                <v-text-field v-model.number="editedPedido.qtd_parcela" label="Qtd Parcelas" type="number"
                  density="compact" variant="outlined" hide-details="auto" min="1" max="12" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" v-if="editedPedido.idformapagamento === 2">
                <v-text-field v-model.number="editedPedido.vl_parcela" label="Valor da Parcela" type="number"
                  density="compact" variant="outlined" hide-details="auto" min="1" max="9999"
                  class="mb-3"></v-text-field>
              </v-col>

              <!-- Quantidade -->
              <v-col cols="12" md="6">
                <v-text-field v-model.number="editedPedido.quantidade" label="Quantidade" type="number"
                  density="compact" variant="outlined" hide-details="auto" min="1" class="mb-3"></v-text-field>
              </v-col>
            </v-row>

            <div class="d-flex justify-end mt-4">
              <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="cancelOrderEdit"
                :disabled="editOrderLoading">
                Cancelar
              </v-btn>
              <v-btn color="primary" type="submit" :loading="editOrderLoading" :disabled="editOrderLoading">
                Salvar
              </v-btn>
            </div>

          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal observação -->
    <v-dialog v-model="observationModalOpen" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white">
          <v-icon start icon="mdi-note-text" class="mr-2"></v-icon>
          {{ observationData.type === 'pedido' ? 'Observação do Pedido' :
            observationData.type === 'revenda' ? 'Observação do Revendedor' :
              'Observação para Revenda' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form @submit.prevent="salvarObservacao">
            <v-textarea v-model="observationData.text" :label="observationData.type === 'pedido' ? 'Observação do Pedido' :
              observationData.type === 'revenda' ? 'Observação do Revendedor' :
                'Observação para Revenda'" rows="5" auto-grow variant="outlined" hide-details="auto"
              class="mb-3"></v-textarea>

            <div class="d-flex justify-end mt-4">
              <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="closeObservationModal">
                Cancelar
              </v-btn>
              <v-btn color="primary" type="submit">
                Salvar
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal solicitar assistência -->
    <v-dialog v-model="assistanceModalOpen" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h5 bg-deep-purple text-white">
          <v-icon start icon="mdi-lifebuoy" class="mr-2"></v-icon>
          Solicitar Assistência
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form @submit.prevent="solicitarAssistencia">
            <v-text-field v-model="assistanceData.idrevenda" label="ID da Revenda" type="number" density="compact"
              variant="outlined" hide-details="auto" class="mb-3" required></v-text-field>

            <v-textarea v-model="assistanceData.motivo" label="Informe a Descrição" rows="5" auto-grow
              variant="outlined" hide-details="auto" class="mb-3" required
              hint="Descreva detalhadamente o problema que precisa de assistência" persistent-hint></v-textarea>

            <div class="d-flex justify-end mt-4">
              <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="cancelAssistance"
                :disabled="assistanceLoading">
                Cancelar
              </v-btn>
              <v-btn color="deep-purple" type="submit" :loading="assistanceLoading"
                :disabled="assistanceLoading || !assistanceData.motivo.trim() || !assistanceData.idrevenda">
                Solicitar
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<style scoped>
/* Organizing styles into logical groups */

/* Layout */
.v-expansion-panel-title {
  padding: 10px 15px;
}

.kanban-container {
  position: relative;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;
  min-height: calc(100vh - 205px);
}

.kanban-wrapper {
  display: flex;
  min-width: min-content;
  gap: 14px;
}

.kanban-column {
  flex: 0 0 320px;
  max-width: 320px;
  min-width: 320px;
  height: calc(100vh - 185px);
  margin-left: 1px;
}

.kanban-cards {
  height: calc(100vh - 250px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Cards */
.order-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.order-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Scrollbars */
.kanban-container::-webkit-scrollbar,
.kanban-cards::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.kanban-container::-webkit-scrollbar-thumb,
.kanban-cards::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.kanban-container::-webkit-scrollbar-track,
.kanban-cards::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Compact tabs */
.compact-tabs :deep(.v-tab) {
  min-width: 0 !important;
  padding: 0 8px !important;
  font-size: 0.9rem !important;
  justify-content: center;
}

.compact-tabs :deep(.v-tabs-bar) {
  height: 32px !important;
}

.compact-tab {
  text-transform: none !important;
  letter-spacing: normal !important;
}

.column-toggle :deep(.v-selection-control__input) {
  opacity: 1 !important;
}

/* Icons */
.copy-icon {
  margin-left: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.copy-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.action-icon {
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.whatsapp-icon {
  color: #25D366 !important;
}

/* Content styling */
.payment-details {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  padding-left: 4px;
  border-left: 2px solid rgba(0, 0, 0, 0.1);
}

.order-observation {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  transition: background-color 0.2s;
}

.order-observation:hover {
  background-color: rgba(0, 0, 0, 0.15);
}

.edit-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.edit-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

/* Drag and drop */
.column-order-item {
  cursor: grab;
  transition: all 0.2s;
  border-radius: 4px;
}

.column-order-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.column-order-dragging {
  opacity: 0.5;
  background-color: rgba(0, 0, 0, 0.1);
}

.column-color-indicator {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

.drag-handle {
  cursor: grab;
  opacity: 0.7;
}

.column-order-item:hover .drag-handle {
  opacity: 1;
}

.visibility-toggle {
  cursor: pointer;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 50%;
}

.visibility-toggle:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.1);
}

.deactivate-option {
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 4px;
  margin-bottom: 4px;
}

.deactivate-option:hover {
  background-color: rgba(var(--v-theme-error), 0.1);
}

.p-0 {
  padding: 0 !important;
}

/* Add styles for the attention column */
.kanban-column.atencao-column .v-card-title {
  background-color: #9C27B0 !important;
  color: white !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .kanban-column {
    flex: 0 0 280px;
    max-width: 280px;
    min-width: 280px;
  }

  .order-card {
    font-size: 0.9rem;
  }
}

/* Accessibility */
.edit-icon:focus-visible,
.copy-icon:focus-visible,
.action-icon:focus-visible {
  outline: 2px solid rgba(var(--v-theme-primary), 0.7);
  border-radius: 4px;
}
</style>